import { useMemo } from 'react';
import useSWR from 'swr';
import type { EmbeddedProduct } from 'modules/supplier/products';
import {
  PRODUCT_DISCOVERY_PAGE_SIZE,
  productDiscoveryRequest,
  productToChainPath,
  type ChainPath,
  type ProductDiscoveryPage,
} from '../ProductChainPicker';
import type { FilterState } from './Filter';

const EMPTY_PRODUCTS: readonly EmbeddedProduct[] = [];

export const SUPPLIER_PRODUCT_PAGE_SIZE = PRODUCT_DISCOVERY_PAGE_SIZE;

export type ChainCatalogue = {
  paths: readonly ChainPath[];
  productsById: Map<number, EmbeddedProduct>;
};

export function supplierProductRequest(
  { type, kind, search, provider }: FilterState,
  outlet_or_service_category: number | undefined,
) {
  return productDiscoveryRequest({
    type,
    kind,
    search,
    outletOrServiceCategory: outlet_or_service_category,
    ...(provider ? { provider: provider.id } : { provided: true }),
  });
}

export function toChainCatalogue(products: readonly EmbeddedProduct[]): ChainCatalogue {
  return {
    paths: products.map(productToChainPath),
    productsById: new Map(products.map((product) => [product.id, product])),
  };
}

/** Keeps the current supplier available when it falls outside bounded discovery results. */
export function includeSelectedProduct(catalogue: ChainCatalogue, product: EmbeddedProduct | null): ChainCatalogue {
  if (!product || catalogue.productsById.has(product.id)) {
    return catalogue;
  }
  return {
    paths: [productToChainPath(product), ...catalogue.paths],
    productsById: new Map(catalogue.productsById).set(product.id, product),
  };
}

/**
 * Queries supplier products for the current discovery controls and returns the
 * raw results for chain normalization.
 *
 * Query behavior is owned by this adapter and must stay stable: type, active
 * kind, search, `disabled=false`, the optional outlet/service-category scope,
 * and either `provider=<id>` when a provider filter is set or `provided=true`
 * when it is empty. The shared picker never issues these queries itself.
 * A null active kind is invalid configuration: nothing is queried and no
 * candidates are offered.
 */
export function useSupplierProducts(
  { type, kind, search, provider }: FilterState,
  outlet_or_service_category: number | undefined,
): ProductDiscoveryPage {
  const key = supplierProductRequest({ type, kind, search, provider }, outlet_or_service_category);
  const { data } = useSWR<ProductDiscoveryPage, unknown>(key, { suspense: true });

  return data ?? { count: 0, results: EMPTY_PRODUCTS };
}

/** Hook binding the supplier query to the shared product chain contract. */
export function useSupplierProductChain(
  filter: FilterState,
  outlet_or_service_category: number | undefined,
): {
  paths: readonly ChainPath[];
  productsById: Map<number, EmbeddedProduct>;
  productCount: number;
  isTruncated: boolean;
} {
  const { count, results } = useSupplierProducts(filter, outlet_or_service_category);
  return useMemo(
    () => ({
      ...toChainCatalogue(results),
      productCount: count,
      isTruncated: count > results.length,
    }),
    [count, results],
  );
}
